import { useCallback, useRef, useState } from 'react';
import { AlertTriangle, FileText, FolderOpen, X } from 'lucide-react';
import './AttachmentPreflightDialog.css';

function formatBytes(value) {
  const bytes = Number(value) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

export function AttachmentPreflightDialog({ preflight, onCancel, onConfirm }) {
  if (!preflight) return null;
  const summary = preflight.summary || {};
  const counts = summary.counts || {};
  const excluded = preflight.excluded || [];
  const violations = preflight.limitViolations || [];
  const roots = preflight.roots || [];
  const blocked = violations.length > 0;
  return (
    <div className="attachment-preflight-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
      <section className="attachment-preflight-dialog" role="dialog" aria-modal="true" aria-label="Review Space attachments">
        <header><div><h2>Review Space attachments</h2><p>This snapshot will be resolved again when the run starts.</p></div><button type="button" onClick={onCancel} aria-label="Close attachment review"><X size={17} /></button></header>
        <div className="attachment-preflight-summary">
          <span><FolderOpen size={14} /><strong>{summary.selectedRoots || roots.length}</strong> roots</span>
          <span><FileText size={14} /><strong>{summary.resolvedNodes || 0}</strong> items</span>
          <span><strong>{formatBytes(summary.textBytes)}</strong> text</span>
        </div>
        <div className="attachment-preflight-counts">
          {['folders', 'text', 'skills', 'image', 'video', 'audio', 'characters'].map((kind) => <span key={kind}><strong>{counts[kind] || 0}</strong>{kind}</span>)}
        </div>
        {roots.length > 0 && <div className="attachment-preflight-section"><h3>Selected</h3>{roots.map((root) => <div key={`${root.kind}:${root.id}`} className="attachment-preflight-row"><span>{root.kind === 'space' ? 'Space' : root.nodeType === 'folder' ? 'Folder' : 'File'}</span><strong>{root.path || root.name}</strong></div>)}</div>}
        {excluded.length > 0 && <div className="attachment-preflight-section warning"><h3><AlertTriangle size={13} /> Excluded or downgraded</h3>{excluded.map((item, index) => <div key={`${item.nodeId}:${index}`} className="attachment-preflight-warning"><strong>{item.path || item.nodeId}</strong><span>{item.reason}{item.attachedAsText ? ' Attached as ordinary text.' : ''}</span></div>)}</div>}
        {violations.length > 0 && <div className="attachment-preflight-section blocked"><h3><AlertTriangle size={13} /> Selection exceeds limits</h3>{violations.map((item) => <div key={item.kind} className="attachment-preflight-warning"><strong>{item.message}</strong><span>{item.actual} selected; {item.allowed} allowed.</span></div>)}</div>}
        <footer><button type="button" onClick={onCancel}>Cancel</button><button type="button" className="primary" disabled={blocked} onClick={onConfirm}>{blocked ? 'Reduce selection' : 'Attach and continue'}</button></footer>
      </section>
    </div>
  );
}

export function useAttachmentPreflightConfirmation() {
  const [preflight, setPreflight] = useState(null);
  const resolverRef = useRef(null);
  const confirm = useCallback((value) => new Promise((resolve) => {
    resolverRef.current = resolve;
    setPreflight(value);
  }), []);
  const finish = useCallback((accepted) => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setPreflight(null);
    resolver?.(accepted);
  }, []);
  return {
    preflight,
    confirm,
    dialog: <AttachmentPreflightDialog preflight={preflight} onCancel={() => finish(false)} onConfirm={() => finish(true)} />,
  };
}
